/**
 * The API client: same-origin `/api` on the web, an absolute base on a phone.
 *
 * Auth is the pairing key in `x-hunsy-key`, exactly as it was — two fixed partner identities, no
 * accounts. `online` drives the reconnecting banner; a network failure is not an error the user
 * needs a dialog about, it is a state the shell shows and recovers from.
 */
import { Platform } from 'react-native';
import { getItem, removeItem, setItem } from './storage';

export const KEY_STORAGE = 'hunsy.key';
export const BASE_STORAGE = 'hunsy.api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Where a phone talks to, when nothing else has said.
 *
 * The web build is served BY the API worker, so it has a same-origin `/api` and needs none of
 * this. A native build has no origin of its own at all — so without a default, a copy opened in
 * Expo Go from a QR code (no pairing link, no env) can reach nothing and shows an unpaired screen
 * it can never leave. This is the deployed app's public address, not a secret.
 */
const DEFAULT_NATIVE_API = 'https://hunsy.pages.dev';

let key: string | null = null;
/**
 * Empty on the web. On a phone the pairing link wins (its own origin, or an `api=` parameter) and
 * is remembered beside the key; EXPO_PUBLIC_HUNSY_API overrides for a dev build pointed at a
 * laptop; otherwise production.
 */
let base = Platform.OS === 'web' ? '' : (process.env.EXPO_PUBLIC_HUNSY_API || DEFAULT_NATIVE_API);

let onlineListener: ((online: boolean) => void) | null = null;

export const api = {
  get key(): string | null {
    return key;
  },
  get base(): string {
    return base;
  },
  /** True once a pairing key exists AND, on native, somewhere to send it. */
  get configured(): boolean {
    return !!key && (Platform.OS === 'web' || !!base);
  },

  async load(): Promise<void> {
    key = await getItem(KEY_STORAGE);
    if (Platform.OS !== 'web') base = (await getItem(BASE_STORAGE)) || base;
  },

  async pair(newKey: string, apiBase?: string): Promise<void> {
    key = newKey;
    await setItem(KEY_STORAGE, newKey);
    if (apiBase && Platform.OS !== 'web') {
      base = apiBase.replace(/\/$/, '');
      await setItem(BASE_STORAGE, base);
    }
  },

  // Unpairing is "this device forgets us", so it forgets the address as well as the key. A base
  // left behind is a note saying which deployment this couple used, kept on a device the person
  // has just said should hold nothing.
  async unpair(): Promise<void> {
    key = null;
    await removeItem(KEY_STORAGE);
    await removeItem(BASE_STORAGE);
  },

  onOnlineChange(fn: (online: boolean) => void): void {
    onlineListener = fn;
  },

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, {
        method,
        headers: {
          'x-hunsy-key': key ?? '',
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      onlineListener?.(false);
      throw new ApiError(0, 'offline');
    }
    onlineListener?.(true);
    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new ApiError(res.status, err?.error ?? `HTTP ${res.status}`);
    }
    // 204s exist on no route the client calls, but a body-less 200 should not throw either.
    return (await res.json().catch(() => null)) as T;
  },

  get: <T>(path: string) => api.request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => api.request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => api.request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => api.request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => api.request<T>('DELETE', path, body),
};
